import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { Network } from '@capacitor/network'
import { App } from '@/app'
import { APIServer } from '@/app/data/api'
import { useAuthStore } from '@/app/data/auth'
import { isApp, GCS_ROOT_PATH } from '@/index'
import { SQL } from '@/lib/Sqlite'
import { prepareSQLiteValue } from '@/lib/Sqlite/schema'
import { SQLiteEventBus, SQLiteUpdateEvent } from '@/lib/sqliteEventBus'
import { getRandomString } from '@/lib/Functions'
import { base64ToFile } from '@/lib/Functions/files'
import { CloudFile } from '@/lib/CloudFile'
import SQLite from '../../../lib/Task/sqlite'
import Merge from '../../../lib/Task/merge'
import { SyncManager } from '@/app/sync/syncManager'
import { syncQueue, JobPriority } from '@/app/sync'
import { DependencyNotReadyError } from '@/app/sync/syncError'
import { filterStaleMergedItems } from '@/app/sync/mergeWriteGuard'
import { isRowDeletedLocally, hasInFlightJob } from '@/app/sync/jobGuards'
import { convertFileToWebp } from '@/app/camera'
import { AppFileManager } from '@/app/file'
import {
  parseRow,
  getLastSortKey,
  sortByKeys,
  uploadPathGenerator,
  handleCapturedPhotoClone,
  syncCacheWithServerUrl,
} from '@/app/util'
import { LocalFileResourcePath } from '../../camera/type'
import { FieldsUpdatedAt, QualityResultPhoto } from '../types'
import {
  FileMissingError,
  registerFileMissing,
  clearFileMissingCount,
  isFileNotFoundError,
  isFileMissingConfirmed,
} from './photoFileGuard'
import { useQualityResultStore } from './qualityResult'

// 품질점검 결과 사진 스토어
// - 로컬 파일(resourcePath) → GCS 업로드 → url 교체 → 서버 레코드 생성 순서 유지
// - localResultId로 결과와 연결 (업로드/병합이 로컬 연결키를 파괴하지 않도록 defectPhoto의
//   localShapeId 처리 방식을 그대로 따른다)
const storeKey = 'quality_result_photos'

// 업로드 필요 url 여부 (로컬 파일 경로 또는 base64)
function needsFileUploadUrl(url: string): boolean {
  return (
    url.startsWith('local-file://') ||
    url.startsWith('data:image') ||
    url.startsWith('android-local-file:/') ||
    url.startsWith('android-file:/')
  )
}

export const useQualityResultPhotoStore = defineStore(storeKey, () => {
  const photos = ref<QualityResultPhoto[]>([])
  const currentTaskDatabaseId = ref<string>('')

  // SQLite 이벤트 리스너 참조 (중복 등록 방지 및 cleanup용)
  let currentListener: ((event: SQLiteUpdateEvent) => void) | null = null

  const notUploadedPhotos = computed(() => {
    return photos.value.filter(item => item.modifiedFromLocal)
  })

  async function uploadNotUploaded(): Promise<void> {
    if (notUploadedPhotos.value.length > 0) {
      await requestUpdate(notUploadedPhotos.value)
    }
  }

  // SQLite 개별 컬럼 갱신 + 이벤트 발행
  async function individualSetLocalModified(
    localStringId: string,
    keyValues: { key: string; value: unknown }[] = [],
  ): Promise<void> {
    const db = await SQL.getDatabase()

    // id 교체 시 동일 localStringId의 유령 row가 남아 있으면 먼저 정리
    // (두 row가 같은 id로 동시 갱신되며 PRIMARY KEY 충돌로 UPDATE 전체가 실패하는 것을 방지)
    if (keyValues.some(keyValue => keyValue.key === 'id')) {
      const duplicated = await db.query(`SELECT * FROM ${storeKey} WHERE localStringId = ?`, [
        localStringId,
      ])
      if (duplicated.values && duplicated.values.length > 1) {
        await removeGhostRows(
          duplicated.values.map((row: unknown) => parseRow(row)) as QualityResultPhoto[],
        )
      }
    }

    let sql = `UPDATE ${storeKey} SET `
    for (let i = 0; i < keyValues.length; i++) {
      sql += `${keyValues[i].key} = ?`
      if (i < keyValues.length - 1) sql += ', '
    }
    sql += ' WHERE localStringId = ?'
    // 값 정규화: iOS native bridge가 number를 TEXT 컬럼에 "557.0"으로 저장하는 것을 차단
    await db.run(sql, [...keyValues.map(item => prepareSQLiteValue(item.value)), localStringId])

    await SQLiteEventBus.getInstance().emitUpdate(storeKey, 'update', [localStringId])
  }

  /**
   * 동일 localStringId의 유령 row(_need_to_upload 잔재)를 SQLite와 메모리에서 정리한다.
   * @param rows 검사할 row 목록
   * @returns 유령 row가 제거된 목록
   */
  async function removeGhostRows(rows: QualityResultPhoto[]): Promise<QualityResultPhoto[]> {
    const byLocalStringId = new Map<string, QualityResultPhoto[]>()
    for (const row of rows) {
      const key = String(row.localStringId)
      const list = byLocalStringId.get(key)
      if (list) list.push(row)
      else byLocalStringId.set(key, [row])
    }

    const ghostIds = new Set<string>()
    const db = await SQL.getDatabase()
    for (const [key, list] of byLocalStringId) {
      if (list.length < 2) continue
      const ghosts = list.filter(row => String(row.id).includes('_need_to_upload'))
      const realRows = list.filter(row => !String(row.id).includes('_need_to_upload'))
      if (ghosts.length === 0 || realRows.length === 0) continue

      console.warn(
        `[PhotoGuard] quality ghost row 정리: localStringId=${key} 유령 ${ghosts.length}개 삭제 (유지 id=${realRows.map(r => r.id).join(',')})`,
      )
      for (const ghost of ghosts) {
        await db.run(`DELETE FROM ${storeKey} WHERE id = ?`, [String(ghost.id)])
        ghostIds.add(String(ghost.id))
      }
      if (realRows.every(row => !row.modifiedFromLocal)) {
        syncQueue.removeJobByLocalStringId(key)
      }
    }

    if (ghostIds.size === 0) return rows
    photos.value = photos.value.filter(photo => !ghostIds.has(String(photo.id)))
    return rows.filter(row => !ghostIds.has(String(row.id)))
  }

  /**
   * SQLite 이벤트 리스너 설정
   * @param taskId 현재 task ID
   */
  function setupSQLiteListener(taskId: string): void {
    if (currentListener) {
      SQLiteEventBus.getInstance().offTableUpdated(storeKey, currentListener)
    }

    currentListener = async (event: SQLiteUpdateEvent): Promise<void> => {
      try {
        if (!SQL.createdTables.has(storeKey)) return
        const db = await SQL.getDatabase()

        if (event.type === 'bulk' || event.ids.length === 0) {
          const localData = await db.query(`SELECT * FROM ${storeKey} WHERE task_id = ?`, [taskId])
          photos.value = (localData.values ?? []).map((row: unknown) =>
            parseRow(row),
          ) as QualityResultPhoto[]
        } else {
          for (const id of event.ids) {
            const result = await db.query(
              `SELECT * FROM ${storeKey} WHERE localStringId = ? AND task_id = ?`,
              [id, taskId],
            )
            if (result.values && result.values.length > 0) {
              const updatedItem = parseRow(result.values[0]) as QualityResultPhoto
              const index = photos.value.findIndex(p => p.localStringId === id)
              if (index !== -1) {
                photos.value[index] = updatedItem
              } else {
                photos.value.push(updatedItem)
              }
            }
          }
        }
      } catch (error) {
        console.error('[QualityResultPhotoStore] Failed to process SQLite event:', error)
      }
    }

    SQLiteEventBus.getInstance().onTableUpdated(storeKey, currentListener)
  }

  async function getServerData(taskId: string): Promise<QualityResultPhoto[]> {
    if (!taskId) return []
    try {
      const data = await APIServer.getQualityResultPhotoListByTaskId(Number(taskId))
      // result_id NULL(FK SET NULL 잔존 행)은 연결 대상이 없어 제외
      return data
        .filter(item => !item.deleted_at && item.result_id !== null)
        .map(item => ({
          ...item,
          localStringId: item.id.toString(),
          localResultId: item.result_id.toString(),
          modifiedFromLocal: false,
        }))
    } catch (error) {
      console.error('[QualityResultPhotoStore] 서버 데이터 조회 실패:', error)
      throw error
    }
  }

  /**
   * taskId 단위 데이터 로드 (온라인: 서버+로컬 병합, 오프라인: 로컬)
   * @param taskId 과업 ID
   * @param signal 취소 시그널
   * @param skipServerSync 동일 과업 재진입 시 서버 병합 스킵
   */
  async function initData(
    taskId: string,
    signal?: AbortSignal,
    skipServerSync = false,
  ): Promise<void> {
    currentTaskDatabaseId.value = taskId

    if (!isApp()) {
      // 웹: SQLite 없이 서버 데이터만 메모리 적재
      if ((await Network.getStatus()).connected) {
        photos.value = await getServerData(taskId)
      } else {
        App.toastInstance.add('error', '네트워크 연결이 필요합니다.')
      }
      return
    }

    setupSQLiteListener(taskId)
    if (skipServerSync) return

    try {
      let mergedData = (await SQLite.getLocalData(
        storeKey,
        'task_id',
        taskId,
        false,
      )) as unknown as QualityResultPhoto[]
      // 동일 localStringId의 유령 row가 남아 있으면 병합 전에 정리 (재업로드 차단)
      mergedData = await removeGhostRows(mergedData)
      if (signal?.aborted) return

      let didMerge = false
      if ((await Network.getStatus()).connected) {
        const serverData = await getServerData(taskId)
        if (signal?.aborted) return

        await SQLite.checkTableExists(storeKey, serverData)

        const activeLocalData = mergedData.filter(item => !item.deleted_at)
        if (serverData.length > 0 || activeLocalData.length === 0) {
          mergedData = Merge.mergeBothData(mergedData, serverData, storeKey)
          didMerge = true
        }
      }

      if (signal?.aborted) return

      if (mergedData.length > 0) {
        // 미업로드 로컬 파일 사진은 다시 업로드 대상으로 플래그 (부재 확정 사진 제외)
        const convertedMergedData = mergedData.map(item => {
          const itemId = String(item.id)
          const itemLocalStringId = String(item.localStringId)
          const needsFileUpload =
            needsFileUploadUrl(item.url) &&
            itemId !== itemLocalStringId &&
            `${itemId}` === `${itemLocalStringId}_need_to_upload` &&
            !item.deleted_at &&
            !isFileMissingConfirmed(itemLocalStringId)
          return {
            ...item,
            modifiedFromLocal: item.modifiedFromLocal || needsFileUpload,
          }
        })
        photos.value = convertedMergedData

        if (didMerge && !SyncManager.isTaskSyncing(taskId)) {
          const { itemsToProcess, columns } = await SQLite.getNeedUpdateItems(
            convertedMergedData,
            storeKey,
          )
          // 병합 중 업로드 job이 교체한 row의 옛 스냅샷은 저장 제외 (유령 row 부활 방지)
          const safeItems = await filterStaleMergedItems(
            itemsToProcess as { item: QualityResultPhoto; itemId: string }[],
            storeKey,
            taskId,
          )
          await SQLite.updateSqliteOnly(
            safeItems,
            columns,
            currentTaskDatabaseId.value,
            storeKey,
            true,
          )
        }
      } else {
        photos.value = mergedData
      }
    } catch (error) {
      if (signal?.aborted) return
      console.error('QualityResultPhotoStore.initData ERROR:', error)
      throw error
    }
  }

  // 결과 매칭용 식별자 집합 (결과 localStringId + 서버 id)
  // 업로드 직후 병합 race로 사진 row가 서버형(localResultId=서버 result id)으로
  // 대체된 경우에도 매칭이 유지되도록 결과의 서버 id를 함께 포함한다.
  const resolveResultMatchIds = (resultDatabaseId: string): Set<string> => {
    const ids = new Set<string>([`${resultDatabaseId}`])
    const resultStore = useQualityResultStore()
    const found = resultStore.GetItem(resultDatabaseId)
    if (found && !String(found.id).includes('_need_to_upload')) {
      ids.add(String(found.id))
    }
    return ids
  }

  // 결과 식별자(result_id·localResultId) → 활성 사진 목록 매핑 (O(1) 조회용 메모이즈)
  const photosByResultKey = computed((): Map<string, QualityResultPhoto[]> => {
    const map = new Map<string, QualityResultPhoto[]>()
    const add = (key: string, photo: QualityResultPhoto): void => {
      const list = map.get(key)
      if (list) list.push(photo)
      else map.set(key, [photo])
    }
    for (const photo of photos.value) {
      if (photo.deleted_at) continue
      const resultKey = `${photo.result_id}`
      const localKey = `${photo.localResultId}`
      add(resultKey, photo)
      if (localKey !== resultKey) add(localKey, photo)
    }
    return map
  })

  /**
   * 특정 결과의 사진 목록 조회
   * @param resultDatabaseId 결과의 localStringId
   * @param photoType NEAR|FAR 필터 (생략 시 전체)
   */
  const getPhotosByResult = (
    resultDatabaseId: string,
    photoType?: 'NEAR' | 'FAR' | 'PLAN',
  ): QualityResultPhoto[] => {
    const resultIds = resolveResultMatchIds(resultDatabaseId)
    const keyedPhotos = photosByResultKey.value
    const result: QualityResultPhoto[] = []
    const seen = new Set<string>()
    for (const key of resultIds) {
      for (const photo of keyedPhotos.get(key) ?? []) {
        const photoKey = String(photo.localStringId)
        if (seen.has(photoKey)) continue
        seen.add(photoKey)
        if (photoType && photo.photo_type !== photoType) continue
        result.push(photo)
      }
    }
    return sortByKeys(result)
  }

  const requestUpdate = async (datas: QualityResultPhoto[]): Promise<void> => {
    if (!isApp()) {
      // 웹: SQLite/syncQueue 우회, APIServer 직접 호출
      await webRequestUpdate(datas)
      return
    }
    const { itemsToProcess, columns } = await SQLite.getNeedUpdateItems(datas, storeKey)
    await SQLite.updateSqliteOnly(itemsToProcess, columns, currentTaskDatabaseId.value, storeKey)
    await addApiQueue(itemsToProcess as { item: QualityResultPhoto; itemId: string }[])
  }

  // 웹 전용: 메모리 항목에 서버 응답을 반영한다
  function applyWebPatch(localStringId: string, patch: Partial<QualityResultPhoto>): void {
    const index = photos.value.findIndex(item => item.localStringId === localStringId)
    if (index !== -1) {
      photos.value[index] = { ...photos.value[index], ...patch }
    }
  }

  /**
   * 웹 전용 저장 경로: 삭제/메타데이터 수정만 처리한다
   * (웹 신규 사진은 AddWithWebFile이 업로드와 레코드 생성을 즉시 수행)
   * @param datas 저장할 사진 목록
   */
  async function webRequestUpdate(datas: QualityResultPhoto[]): Promise<void> {
    for (const data of datas) {
      const isNew = String(data.id).includes('_need_to_upload')
      const isDeleted = !!data.deleted_at
      if (isDeleted) {
        // 서버에 생성된 적 없는 사진은 서버 호출 없이 종료 (웹에서는 발생하지 않아야 함)
        if (!isNew) {
          await APIServer.deleteQualityResultPhotos([{ id: Number(data.id) }])
        }
        applyWebPatch(data.localStringId, { modifiedFromLocal: false })
      } else if (isNew) {
        // 방어: 웹 신규 사진은 AddWithWebFile 경로만 사용 - 여기 도달하면 규약 위반
        console.warn('[QualityResultPhoto] 웹에서는 AddWithWebFile로만 사진을 추가할 수 있습니다.')
      } else {
        const [photoResult] = await APIServer.updateQualityResultPhotos([
          {
            id: Number(data.id),
            caption: data.caption,
            photo_type: data.photo_type,
            set_no: data.set_no ?? 1,
            sort_key: data.sort_key,
            fields_updated_at: data.fields_updated_at,
          },
        ])
        applyWebPatch(data.localStringId, {
          updated_at: photoResult?.updated_at ?? data.updated_at,
          modifiedFromLocal: false,
        })
      }
    }
  }

  /**
   * 웹 전용: webp Blob을 GCS에 업로드하고 서버 레코드를 즉시 생성한다
   * (앱의 업로드 경로 규약 task/{taskId}/quality_result/{파일명}을 그대로 따른다)
   * @param resultDatabaseId 결과의 localStringId 또는 서버 id
   * @param blob webp로 변환된 이미지 Blob
   * @param photoType NEAR(근거리) | FAR(원거리)
   * @param setNo 기록 세트 번호 (1~3, 도면은 1)
   * @param caption 사진 설명
   * @returns 서버에 생성된 사진
   */
  async function AddWithWebFile(
    resultDatabaseId: string,
    blob: Blob,
    photoType: 'NEAR' | 'FAR' | 'PLAN',
    setNo: number = 1,
    caption: string = '',
  ): Promise<QualityResultPhoto> {
    const resultStore = useQualityResultStore()
    const row = resultStore.GetItem(resultDatabaseId)
    const serverResultId = Number(row?.id ?? resultDatabaseId)
    // 방어: 웹은 결과 Add가 즉시 서버 id를 받으므로 이 경우는 발생하지 않아야 함
    if (!row || String(row.id).includes('_need_to_upload') || isNaN(serverResultId)) {
      throw new Error('결과가 아직 서버에 저장되지 않았습니다.')
    }

    const localId = getRandomString(12, false)
    const file = new File([blob], `${serverResultId}_${localId}_${Date.now()}.webp`, {
      type: 'image/webp',
    })

    // 1단계: 파일을 버킷에 업로드 (앱 업로드 경로 규약과 동일)
    const taskDatabaseId = String(row.task_id || currentTaskDatabaseId.value)
    const uploadPath = uploadPathGenerator(taskDatabaseId)
    const resourceUrl = await CloudFile.uploadFile(
      'homecheck-service',
      `${GCS_ROOT_PATH}/${uploadPath}/quality_result/${file.name}`,
      file,
    )
    if (!resourceUrl || !resourceUrl.startsWith('storage://')) {
      throw new Error('사진 업로드에 실패했습니다.')
    }

    // 2단계: 업로드 성공 후 서버 레코드 생성
    const [photoResult] = await APIServer.addQualityResultPhotos([
      Merge.returnHasValue({
        result_id: serverResultId,
        url: resourceUrl,
        photo_type: photoType,
        set_no: setNo,
        caption,
        sort_key: getLastSortKey(getPhotosByResult(resultDatabaseId, photoType)),
      }) as Partial<QualityResultPhoto>,
    ])
    if (!photoResult || !photoResult.id) {
      throw new Error('사진 레코드 생성에 실패했습니다.')
    }

    const photo: QualityResultPhoto = {
      ...photoResult,
      url: resourceUrl,
      localStringId: String(photoResult.id),
      localResultId: String(photoResult.result_id),
      modifiedFromLocal: false,
    }
    photos.value.push(photo)
    return photo
  }

  async function onlySetSqlite(items: QualityResultPhoto[]): Promise<void> {
    const { itemsToProcess, columns } = await SQLite.getNeedUpdateItems(items, storeKey)
    await SQLite.updateSqliteOnly(itemsToProcess, columns, currentTaskDatabaseId.value, storeKey)
  }

  const addApiQueue = async (
    itemsToProcess: { item: QualityResultPhoto; itemId: string }[],
  ): Promise<void> => {
    if (itemsToProcess.length === 0) return
    for (const { item } of itemsToProcess) {
      // 로컬에서 변경된 항목만 동기화 대상으로 삼는다
      if (!item.modifiedFromLocal) continue
      await addPhotoJobToQueue(item)
    }
  }

  /**
   * 결과의 서버 id를 조회한다 (defectPhoto의 findShapeId와 동일 개념).
   * @param resultKey 결과의 localStringId 또는 id
   * @returns 서버 id / 'is_deleted' / '_need_to_upload'
   */
  async function findResultId(resultKey: string): Promise<number | string> {
    const db = await SQL.getDatabase()
    const stripped = resultKey.replace('_need_to_upload', '')
    const result = await db.query(
      'SELECT id, deleted_at FROM quality_inspection_results WHERE localStringId = ? OR id = ? OR localStringId = ? OR id = ?',
      [resultKey, resultKey, stripped, stripped],
    )
    const row = result.values?.[0]
    if (!row) return '_need_to_upload'
    if (row.deleted_at) return 'is_deleted'
    return row.id
  }

  /**
   * 파일 부재 사진 처리: 유예 중에는 FileMissingError로 재시도를 유도하고,
   * 부재 확정 시 동기화 대상에서 제외한다
   * @param data 대상 사진
   * @param localFilePath 누락된 로컬 파일 경로
   * @returns 확정 시 동기화 종료 처리된 사진 (job 성공으로 처리)
   */
  async function handleMissingPhotoFile(
    data: QualityResultPhoto,
    localFilePath: string,
  ): Promise<QualityResultPhoto> {
    if (registerFileMissing(data.localStringId, '품질점검') === 'retry') {
      throw new FileMissingError(`File not found: ${localFilePath}`)
    }
    await individualSetLocalModified(data.localStringId, [
      { key: 'modifiedFromLocal', value: false },
    ])
    return { ...data, modifiedFromLocal: false }
  }

  /**
   * 사진 동기화 작업을 SyncQueue에 추가
   * @param item 동기화할 사진 데이터
   */
  async function addPhotoJobToQueue(item: QualityResultPhoto): Promise<void> {
    const isNew = String(item.id).includes('_need_to_upload')
    // 자동 플래그(병합/404 처리)가 아닌 사용자 의도 삭제만 서버 DELETE로 전파
    const isDeleted = item.deleted_at && !!item.fields_updated_at?.deleted_at
    const needsFileUpload = needsFileUploadUrl(item.url)

    // 동일 항목의 기존 job 처리(PENDING 갱신·PROCESSING 대기열)는 syncQueue.addJob이 담당
    // - 여기서 스킵하면 PROCESSING 완료 시점의 mfl=false 덮어쓰기로 새 편집이 유실됨
    // - 삭제 요청도 addJob 경유로 업로드 완료 후 이어서 서버에 전파됨

    let executeCallback: (data: QualityResultPhoto) => Promise<QualityResultPhoto | void>
    let jobPriority = JobPriority.NORMAL

    if (isDeleted) {
      // 사진이 삭제되면 파일 부재 이력도 함께 정리
      clearFileMissingCount(item.localStringId)
      // 서버에 생성된 적 없고 진행 중인 업로드 job도 없는 사진은 서버에 알릴 필요가 없음
      if (isNew && !hasInFlightJob(item.localStringId)) {
        await individualSetLocalModified(item.localStringId, [
          { key: 'modifiedFromLocal', value: false },
        ])
        return
      }
      executeCallback = async (data: QualityResultPhoto): Promise<QualityResultPhoto> => {
        // 실행 시점 기준 서버 미생성 사진(업로드가 삭제로 무효화된 경우): 서버 호출 없이 종료
        if (String(data.id).includes('_need_to_upload')) {
          await individualSetLocalModified(data.localStringId, [
            { key: 'modifiedFromLocal', value: false },
          ])
          return data
        }
        await APIServer.deleteQualityResultPhotos([{ id: Number(data.id) }])
        await individualSetLocalModified(data.localStringId, [
          { key: 'modifiedFromLocal', value: false },
        ])
        return data
      }
    } else if (isNew) {
      jobPriority = JobPriority.LOW
      executeCallback = async (data: QualityResultPhoto): Promise<QualityResultPhoto> => {
        // 업로드 전에 로컬에서 삭제된 사진: 서버에 생성하지 않고 동기화 종료
        if (data.deleted_at) {
          await individualSetLocalModified(data.localStringId, [
            { key: 'modifiedFromLocal', value: false },
          ])
          return data
        }

        // 의존성 처리: 결과의 서버 id 확정 후 업로드 (defectPhoto의 shape 대기 패턴)
        const localResultId = data.localResultId || String(data.result_id)
        const resultId = await findResultId(localResultId)
        if (resultId === 'is_deleted') {
          // 결과가 삭제됨 → 사진도 로컬 삭제 처리
          const photoDeleteTime = Merge.getCurrentTime()
          await individualSetLocalModified(data.localStringId, [
            { key: 'deleted_at', value: photoDeleteTime },
            { key: 'modifiedFromLocal', value: false },
          ])
          return { ...data, deleted_at: photoDeleteTime, modifiedFromLocal: false }
        }
        if (String(resultId).includes('_need_to_upload') || isNaN(Number(resultId))) {
          throw new DependencyNotReadyError(`Dependent result ${localResultId} is not synced yet.`)
        }

        if (needsFileUpload) {
          // storage://로 시작하면 이미 GCS 업로드 완료 → 서버 레코드만 생성
          if (data.url.startsWith('storage://')) {
            return createPhotoRecord(data, Number(resultId), data.url)
          }

          const tempFileName = `${resultId}_${data.localStringId}_${Date.now()}.webp`
          let file: File | undefined
          let localFilePath: LocalFileResourcePath | undefined

          if (
            data.url.startsWith('local-file://') ||
            data.url.startsWith('android-local-file:/') ||
            data.url.startsWith('android-file:/')
          ) {
            localFilePath = data.url as LocalFileResourcePath

            // 파일 존재 확인 (확인된 404만 missing 판정)
            if (!(await AppFileManager.checkFileExists(localFilePath))) {
              return handleMissingPhotoFile(data, localFilePath)
            }

            let webpBase64: string | undefined
            try {
              const converted = await convertFileToWebp({ resourcePath: localFilePath })
              if (!converted.value) {
                throw new Error('File conversion to WebP failed.')
              }
              webpBase64 = converted.value
            } catch (error) {
              // 사전 확인이 통과해도 네이티브 변환의 파일 없음(ENOENT)은 부재 처리로 위임
              if (!isFileNotFoundError(error)) throw error
              return handleMissingPhotoFile(data, localFilePath)
            }
            clearFileMissingCount(data.localStringId)
            file = await base64ToFile(webpBase64, tempFileName)
          } else if (data.url.startsWith('data:image')) {
            file = await base64ToFile(data.url, tempFileName)
          }

          if (!file) throw new Error('File conversion failed for new quality photo.')

          const taskDatabaseId = String(data.task_id || currentTaskDatabaseId.value || 'local')
          const uploadPath = uploadPathGenerator(taskDatabaseId)
          const typeUrl = `${uploadPath}/quality_result/${file.name}`

          // 1단계: 파일을 버킷에 먼저 업로드
          const resourceUrl = await CloudFile.uploadFile(
            'homecheck-service',
            `${GCS_ROOT_PATH}/${typeUrl}`,
            file,
          )
          if (!resourceUrl) throw new Error('Cloud upload failed for new quality photo.')
          if (!resourceUrl.startsWith('storage://')) {
            throw new Error(`Upload failed: invalid URL format (${resourceUrl})`)
          }

          // 2단계: GCS 업로드 성공 시 SQLite와 job 데이터에 즉시 url 반영
          // 이후 재시도가 storage:// 분기로 진입해 파일 재업로드·서버 레코드 중복 생성을 막는다
          await individualSetLocalModified(data.localStringId, [{ key: 'url', value: resourceUrl }])
          data.url = resourceUrl

          // 촬영 사진을 앱 디렉토리로 복사 + 서버 파일명으로 캐시 동기화 (오프라인 뷰어 대응)
          if (localFilePath) {
            await handleCapturedPhotoClone({ ...data, resourcePath: localFilePath })
            await syncCacheWithServerUrl(localFilePath, resourceUrl)
          }

          // 3단계: 파일 업로드 성공 후, 버킷 경로로 레코드 생성
          return createPhotoRecord(data, Number(resultId), resourceUrl)
        }

        // 파일 업로드가 필요 없는 경우 (드문 케이스)
        return createPhotoRecord(data, Number(resultId), data.url)
      }
    } else {
      // 메타데이터 업데이트 (caption, sort_key 등)
      executeCallback = async (data: QualityResultPhoto): Promise<QualityResultPhoto> => {
        const [photoResult] = await APIServer.updateQualityResultPhotos([
          {
            id: Number(data.id),
            caption: data.caption,
            photo_type: data.photo_type,
            set_no: data.set_no ?? 1,
            sort_key: data.sort_key,
            fields_updated_at: data.fields_updated_at,
          },
        ])
        return photoResult
      }
    }

    syncQueue.addJob({
      localStringId: item.localStringId,
      data: item,
      priority: jobPriority,
      maxRetries: 5,
      tag: 'qualityResultPhoto',
      execute: executeCallback,
      onSuccess: async (result, data) => {
        if (!isNew) {
          const updateValues: { key: string; value: unknown }[] = [
            { key: 'modifiedFromLocal', value: false },
          ]
          if (needsFileUpload && result?.url) {
            updateValues.push({ key: 'url', value: result.url })
          }
          await individualSetLocalModified(data.localStringId, updateValues)
        }
      },
      onError: async (error, data, errorCode) => {
        if (!(error instanceof FileMissingError)) {
          console.error(
            `[PhotoGuard] qualityResultPhoto job failed id=${data.localStringId}: ${error instanceof Error ? error.message : String(error)}`,
          )
        }
        // 404(서버 삭제)/409(중복): 로컬 삭제 처리 후 큐에서 제거
        if (errorCode === 404 || errorCode === 409) {
          await individualSetLocalModified(data.localStringId, [
            { key: 'deleted_at', value: Merge.getCurrentTime() },
            { key: 'modifiedFromLocal', value: false },
          ])
          syncQueue.removeJobByLocalStringId(data.localStringId)
          return
        }
        // 403(권한 거부): 로컬 변경을 포기하고 큐에서 제거 (이후 병합이 서버 상태 복원)
        if (errorCode === 403) {
          console.warn(
            `[QualityResultPhoto] 권한 거부(403) - 로컬 변경 폐기: ${data.localStringId}`,
          )
          if (isNew) {
            // 생성 자체가 거부됨 → 로컬-only 행을 삭제 처리 (유령 행·의존 job 무한 대기 방지)
            await individualSetLocalModified(data.localStringId, [
              { key: 'deleted_at', value: Merge.getCurrentTime() },
              { key: 'modifiedFromLocal', value: false },
            ])
          } else {
            // 수정 거부 → 필드 스탬프를 폐기해 다음 병합이 서버 상태로 복원하게 함
            await individualSetLocalModified(data.localStringId, [
              { key: 'modifiedFromLocal', value: false },
              { key: 'fields_updated_at', value: JSON.stringify({}) },
            ])
          }
          syncQueue.removeJobByLocalStringId(data.localStringId)
          return
        }
        console.error(`[SyncQueue] QualityResultPhoto job for ${data.localStringId} FAILED.`, error)
      },
      onIdMap: isNew
        ? async (localStringId: string, serverResult: QualityResultPhoto): Promise<void> => {
            try {
              if (!serverResult || !serverResult.id) {
                throw new Error('Invalid serverResult: missing id or data')
              }
              // 메모리 상의 데이터 업데이트 - localResultId는 절대 덮어쓰지 않는다
              const photoInStore = photos.value.find(p => p.localStringId === localStringId)
              if (photoInStore) {
                photoInStore.id = serverResult.id
                photoInStore.result_id = serverResult.result_id
                photoInStore.inspection_id = serverResult.inspection_id
                photoInStore.url = serverResult.url
                photoInStore.modifiedFromLocal = false
              }
            } catch (error) {
              console.error('[onIdMap] serverResult:', JSON.stringify(serverResult))
              throw error
            }
          }
        : undefined,
    })
  }

  /**
   * 서버 레코드 생성 + 로컬 id 매핑 (localResultId 연결키는 유지)
   * @param data 원본 사진 데이터
   * @param resultId 확정된 결과 서버 id
   * @param url 업로드 완료된 storage:// URL
   */
  async function createPhotoRecord(
    data: QualityResultPhoto,
    resultId: number,
    url: string,
  ): Promise<QualityResultPhoto> {
    const photoData = {
      result_id: resultId,
      url,
      photo_type: data.photo_type,
      set_no: data.set_no ?? 1,
      caption: data.caption,
      sort_key: data.sort_key,
    }
    const [photoResult] = await APIServer.addQualityResultPhotos([
      Merge.returnHasValue(photoData) as Partial<QualityResultPhoto>,
    ])
    if (!photoResult || !photoResult.id) {
      throw new Error('Failed to create quality photo record on server.')
    }

    // 서버 확정값 반영 - localResultId는 절대 갱신하지 않는다 (로컬 연결 유지)
    const updateKeys: { key: string; value: unknown }[] = [
      { key: 'id', value: photoResult.id },
      { key: 'result_id', value: photoResult.result_id },
    ]
    if (photoResult.inspection_id) {
      updateKeys.push({ key: 'inspection_id', value: photoResult.inspection_id })
    }
    // 업로드 도중 로컬 삭제가 끼어든 경우 mfl을 유지해 삭제 job이 이어서 전파
    if (!(await isRowDeletedLocally(storeKey, data.localStringId))) {
      updateKeys.push({ key: 'modifiedFromLocal', value: false })
    }
    await individualSetLocalModified(data.localStringId, updateKeys)

    return { ...photoResult, url, localResultId: data.localResultId }
  }

  /**
   * 로컬 파일 경로로 사진 추가 (촬영/선택 직후 호출)
   * @param resultDatabaseId 결과의 localStringId
   * @param inspectionDatabaseId 세션의 localStringId (연결 참고용)
   * @param localFilePath 로컬 파일 경로
   * @param photoType NEAR(근거리) | FAR(원거리)
   * @param setNo 기록 세트 번호 (1~3, 도면은 1)
   * @param localPhotoDatabaseId 사진 localStringId (미리 발급된 경우)
   * @param caption 사진 설명
   */
  async function AddWithLocalFilePath(
    resultDatabaseId: string,
    inspectionDatabaseId: string,
    localFilePath: LocalFileResourcePath,
    photoType: 'NEAR' | 'FAR' | 'PLAN',
    setNo: number = 1,
    localPhotoDatabaseId?: string,
    caption: string = '',
  ): Promise<QualityResultPhoto> {
    const localId = localPhotoDatabaseId ?? getRandomString(12, false)
    const currentTime = Merge.getCurrentTime()
    const initialFieldsUpdatedAt: FieldsUpdatedAt = {
      url: currentTime,
      photo_type: currentTime,
      set_no: currentTime,
      caption: currentTime,
      localResultId: currentTime,
      sort_key: currentTime,
    }

    const photo: QualityResultPhoto = {
      id: `${localId}_need_to_upload`,
      result_id: -1,
      inspection_id: inspectionDatabaseId,
      task_id: Number(currentTaskDatabaseId.value),
      // 로컬 생성 행도 전역 동기화의 계정 필터에 매칭되도록 실제 company_id 부여
      company_id: useAuthStore().companyId ?? 0,
      created_user_id: 0,
      url: localFilePath,
      photo_type: photoType,
      set_no: setNo,
      caption,
      sort_key: getLastSortKey(getPhotosByResult(resultDatabaseId, photoType)),
      fields_updated_at: initialFieldsUpdatedAt,
      created_at: currentTime,
      updated_at: currentTime,
      deleted_at: null,
      localResultId: resultDatabaseId,
      localStringId: localId,
      modifiedFromLocal: true,
    }

    if (!isApp()) {
      photos.value.push(photo)
    }
    await requestUpdate([photo])
    return photo
  }

  /**
   * 사진 메타데이터 수정 (caption 등)
   * @param localStringId 대상 사진의 localStringId
   * @param changes 변경 필드
   */
  async function UpdateValue(
    localStringId: string,
    changes: Partial<QualityResultPhoto>,
  ): Promise<void> {
    const current = photos.value.find(item => item.localStringId === localStringId)
    if (!current) return

    const changed: QualityResultPhoto = {
      ...current,
      ...changes,
      fields_updated_at: { ...current.fields_updated_at },
    }
    changed.fields_updated_at = Merge.findChangedField(current, changed)
    changed.modifiedFromLocal = true

    if (!isApp()) {
      const index = photos.value.findIndex(item => item.localStringId === localStringId)
      if (index !== -1) photos.value[index] = changed
    }
    await requestUpdate([changed])
  }

  /**
   * 사진 삭제 (soft-delete 후 서버 전파)
   * @param localStringId 대상 사진의 localStringId
   */
  async function Delete(localStringId: string): Promise<void> {
    const current = photos.value.find(item => item.localStringId === localStringId)
    if (!current) return

    const target: QualityResultPhoto = {
      ...current,
      fields_updated_at: { ...current.fields_updated_at },
    }
    target.deleted_at = Merge.getCurrentTime()
    const fieldsUpdatedAt = Merge.setFieldUpdate(target.fields_updated_at, 'deleted_at')
    fieldsUpdatedAt.need_restore = false
    target.fields_updated_at = fieldsUpdatedAt
    target.modifiedFromLocal = true

    if (!isApp()) {
      const index = photos.value.findIndex(item => item.localStringId === localStringId)
      if (index !== -1) photos.value[index] = target
    }
    await requestUpdate([target])
  }

  /**
   * 업로드되지 않은 사진들을 결과 삭제와 함께 정리 (defectPhoto의 deleteNotUpload 패턴)
   * @param resultLocalStringId 결과의 localStringId
   * @param deletedTime 삭제 시간
   */
  async function deleteNotUploadPhotosByResult(
    resultLocalStringId: string,
    deletedTime: string,
  ): Promise<void> {
    const findPhotos = photos.value.filter(
      photo =>
        `${photo.localResultId}` === `${resultLocalStringId}` &&
        String(photo.id).includes('_need_to_upload') &&
        !photo.deleted_at &&
        photo.modifiedFromLocal,
    )
    if (findPhotos.length === 0) return

    const db = await SQL.getDatabase()
    for (const photo of findPhotos) {
      await db.run(
        `UPDATE ${storeKey} SET deleted_at = ?, modifiedFromLocal = ? WHERE localStringId = ?`,
        [deletedTime, 0, photo.localStringId],
      )
      syncQueue.removeJobByLocalStringId(photo.localStringId)
    }
    await SQLiteEventBus.getInstance().emitUpdate(
      storeKey,
      'delete',
      findPhotos.map(photo => photo.localStringId),
    )
  }

  async function getAllModifiedData(): Promise<QualityResultPhoto[]> {
    const db = await SQL.getDatabase()
    if (!SQL.createdTables.has(storeKey)) return []
    const localData = await db.query(`SELECT * FROM ${storeKey} WHERE modifiedFromLocal = 1`)
    return (localData.values ?? []).map((row: unknown) => parseRow(row)) as QualityResultPhoto[]
  }

  function allClear(): void {
    if (currentListener) {
      SQLiteEventBus.getInstance().offTableUpdated(storeKey, currentListener)
      currentListener = null
    }
    photos.value = []
    currentTaskDatabaseId.value = ''
  }

  return {
    photos,
    notUploadedPhotos,
    initData,
    getPhotosByResult,
    AddWithLocalFilePath,
    AddWithWebFile,
    UpdateValue,
    Delete,
    deleteNotUploadPhotosByResult,
    requestUpdate,
    uploadNotUploaded,
    addApiQueue,
    onlySetSqlite,
    getAllModifiedData,
    individualSetLocalModified,
    allClear,
  }
})
